import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Plot from "react-plotly.js";
import axios from "axios";
import CytoscapeComponent from "react-cytoscapejs";
import cytoscape from "cytoscape";
import fcose from "cytoscape-fcose";
import { Toast } from "primereact/toast";

cytoscape.use(fcose);

const colorFromString = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 360;
  const s = 60 + (str.length % 20);
  const l = 45 + (str.length % 6);
  return `hsl(${h}, ${s}%, ${l}%)`;
};

const SectionTitle = ({ title, subtitle }) => (
  <div style={{ margin: "6px 0 10px" }}>
    <h3 style={{ margin: 0 }}>{title}</h3>
    {subtitle && <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>{subtitle}</div>}
  </div>
);

const KPI = ({ label, value }) => (
  <div style={{
    border: "1px solid #eaeaea",
    borderRadius: 14,
    padding: 16,
    background: "#fff",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)"
  }}>
    <div style={{ fontSize: 12, opacity: 0.7 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>{value}</div>
  </div>
);

const SimpleTable = ({ columns, rows, empty = "No data" }) => (
  <div style={{ overflowX: "auto", border: "1px solid #eee", borderRadius: 10 }}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ background: "#f7f7f9" }}>
          {columns.map((c) => (
            <th key={c.key} style={{ textAlign: c.align || "left", padding: "10px 12px", fontSize: 12, fontWeight: 700, borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length ? rows.map((r, i) => (
          <tr key={i} style={{ borderBottom: "1px solid #f1f1f1" }}>
            {columns.map((c) => (
              <td key={c.key} style={{ padding: "10px 12px", fontSize: 13, textAlign: c.align || "left", whiteSpace: "nowrap" }}>
                {typeof c.render === "function" ? c.render(r) : r[c.key]}
              </td>
            ))}
          </tr>
        )) : (
          <tr><td colSpan={columns.length} style={{ padding: 16, opacity: 0.6 }}>{empty}</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const MetadataTab = ({ baseUrl, selectedTitle, setSelectedTitle, titles, source }) => {
  const toast = useRef(null);
  const cyRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  const fetchMeta = useCallback(async () => {
    if (!selectedTitle) return;
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        source,
        title: selectedTitle,
        dialogue_limit: "100000",
        dialogue_offset: "0",
        include_text: "0",
      });
      const res = await axios.get(`${baseUrl}/get-script-metadata/?${params.toString()}`);
      setMeta(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, selectedTitle, source]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  // ===== Quantitativos
  const counts = meta?.counts || {};
  const kpis = [
    { label: "Characters", value: counts.characters ?? 0 },
    { label: "Scenes", value: counts.scenes ?? 0 },
    { label: "Dialogues", value: counts.dialogues_total ?? 0 },
    { label: "Locations", value: counts.locations ?? 0 },
  ];

  // ===== Falas por personagem (apenas quem realmente falou)
  const charLines = useMemo(() => {
    if (!meta?.scenes || !meta?.characters) return [];
    const nameById = new Map(meta.characters.map(c => [c.id, {
      name: c.canonical_name || c.name || "UNKNOWN",
      importance: c.importance
    }]));
    const counts = new Map(); // id -> #lines
    for (const sc of meta.scenes) {
      for (const d of (sc.dialogues || [])) {
        const id = d.character_id;
        if (!id) continue;
        counts.set(id, (counts.get(id) || 0) + 1);
      }
    }
    const total = Array.from(counts.values()).reduce((a,b)=>a+b,0) || 1;
    const arr = Array.from(counts.entries()).map(([id, n]) => ({
      id,
      name: nameById.get(id)?.name || String(id),
      importance: nameById.get(id)?.importance ?? null,
      lines: n,
      share: n / total,
    }));
    arr.sort((a,b) => b.lines - a.lines || a.name.localeCompare(b.name));
    return arr;
  }, [meta]);

  // ===== Cenas por local
  const scenesPerLoc = useMemo(() => {
    if (!meta?.scenes) return [];
    const map = new Map();
    for (const sc of meta.scenes) {
      const locName = sc.location?.name || "UNKNOWN";
      map.set(locName, (map.get(locName) || 0) + 1);
    }
    const arr = Array.from(map.entries()).map(([name, n]) => ({ name, scenes: n }));
    arr.sort((a,b)=> b.scenes - a.scenes || a.name.localeCompare(b.name));
    return arr;
  }, [meta]);

  // ===== Time of day (pizza)
  const timeOfDayDist = useMemo(() => {
    if (!meta?.scenes) return [];
    const map = new Map();
    for (const sc of meta.scenes) {
      const t = (sc.time_of_day || "UNKNOWN").toUpperCase();
      map.set(t, (map.get(t) || 0) + 1);
    }
    return Array.from(map.entries()).map(([label, value]) => ({ label, value }));
  }, [meta]);

  // ===== Grafo (Top N falantes) — CALCULE ANTES do layout
  const graphData = useMemo(() => {
    if (!meta?.scenes) return { nodes: [], links: [] };
    const TOP_N = 12;

    const topIds = new Set(charLines.slice(0, TOP_N).map(x => x.id));
    const nameById = new Map(charLines.map(c => [c.id, c.name]));
    const linesById = new Map(charLines.map(c => [c.id, c.lines]));

    const pairCount = new Map();
    for (const sc of meta.scenes) {
      const setChars = new Set(
        (sc.dialogues || [])
          .map(d => d.character_id)
          .filter(id => id && topIds.has(id))
      );
      const ids = Array.from(setChars);
      for (let i = 0; i < ids.length; i++) {
        for (let j = i + 1; j < ids.length; j++) {
          const a = ids[i], b = ids[j];
          const key = a < b ? `${a}-${b}` : `${b}-${a}`;
          pairCount.set(key, (pairCount.get(key) || 0) + 1);
        }
      }
    }

    const nodes = Array.from(topIds).map(id => {
      const name = nameById.get(id) || String(id);
      return {
        id: String(id),
        name,
        lines: linesById.get(id) || 1,
        color: colorFromString(name),
        deg: 0,
      };
    });

    const links = [];
    for (const [key, n] of pairCount.entries()) {
      if (n >= 1) {
        const [a, b] = key.split("-").map(String);
        links.push({ source: a, target: b, count: n });
      }
    }

    // compute degree
    const idxById = new Map(nodes.map((n, i) => [n.id, i]));
    for (const l of links) {
      const si = idxById.get(String(l.source));
      const ti = idxById.get(String(l.target));
      if (si != null) nodes[si].deg += 1;
      if (ti != null) nodes[ti].deg += 1;
    }

    return { nodes, links };
  }, [meta, charLines]);

  // ===== Cytoscape elements
  const cyElements = useMemo(() => {
    const nodes = (graphData.nodes || []).map(n => ({
      data: {
        id: String(n.id),
        label: n.name,
        lines: n.lines || 1,
        deg: n.deg || 0,
        color: n.color || "#3b82f6",
      }
    }));

    const edges = (graphData.links || []).map(l => ({
      data: {
        id: `${l.source}-${l.target}`,
        source: String(l.source),
        target: String(l.target),
        weight: l.count || 1
      }
    }));

    return [...nodes, ...edges];
  }, [graphData]);

  // ===== Layout (fcose) forte contra sobreposição
  const cyLayout = useMemo(() => ({
    name: "fcose",
    quality: "default",
    randomize: true,
    animate: true,
    animationDuration: 700,
    fit: false,
    nodeRepulsion: node => 8000 + 400 * (node.data("deg") || 0) + 12 * (node.data("lines") || 1),
    idealEdgeLength: edge => 120 + 24 * (edge.data("weight") || 1),
    edgeElasticity: edge => 0.03 + 0.005 * (edge.data("weight") || 1),
    nodeSeparation: 110,
    gravity: 0.25,
    gravityRangeCompound: 1.0,
    gravityCompound: 1.0,
    packComponents: true,
    uniformNodeDimensions: false
  }), []);

  // ===== Stylesheet (labels legíveis, nós proporcionais, arestas por peso)
  const cyStylesheet = useMemo(() => ([
    {
      selector: "node",
      style: {
        "width": "mapData(lines, 1, 200, 14, 46)",
        "height": "mapData(lines, 1, 200, 14, 46)",
        "background-color": "data(color)",
        "border-width": 1,
        "border-color": "#00000066",
        "label": "data(label)",
        "font-size": "mapData(lines, 1, 200, 10, 16)",
        "color": "#111",
        "text-wrap": "wrap",
        "text-max-width": 160,
        "text-outline-color": "#ffffff",
        "text-outline-width": 3,
        "z-index-compare": "manual",
        "z-index": 10
      }
    },
    {
      selector: "edge",
      style: {
        "width": "mapData(weight, 1, 15, 2, 8)",
        "line-color": "#1f293b59",
        "curve-style": "bezier"
      }
    },
    { selector: "node:hover", style: { "border-width": 2, "border-color": "#00000099", "z-index": 50 } },
    { selector: "edge:hover", style: { "line-color": "#1f293b99", "width": 8, "z-index": 40 } }
  ]), []);

  // refit a cada dado novo
  useEffect(() => {
    if (!cyRef.current) return;
    try {
      const cy = cyRef.current;
      const layout = cy.layout(cyLayout);
      layout.run();
      setTimeout(() => cy.fit(undefined, 60), 350);
    } catch (e) {
      // noop
    }
  }, [cyElements, cyLayout]);

  const onNodeClick = useCallback((node) => {
    toast.current?.show({
      severity: "info",
      summary: node.name,
      detail: `Falas: ${node.lines}`,
      life: 3500
    });
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Toast ref={toast} />
      {/* KPIs */}
      <SectionTitle title="Overview — Quantitativos" subtitle="Totais de elementos extraídos do roteiro." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
        {kpis.map(k => <KPI key={k.label} label={k.label} value={k.value} />)}
      </div>

      {error && <div style={{ color: "crimson" }}>Erro: {String(error)}</div>}
      {loading && <div>Carregando metadados…</div>}

      {meta && !loading && (
        <>
          {/* Characters + Time of day */}
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
            <div>
              <SectionTitle
                title="Characters (com falas capturadas)"
                subtitle="Nome, número de falas e participação relativa. Ordenado por #Lines."
              />
              <SimpleTable
                columns={[
                  { key: "rank", label: "#", align: "right", render: (r, i) => i + 1 },
                  { key: "name", label: "Name", render: (r) => r.name },
                  { key: "lines", label: "#Lines", align: "right", render: (r) => r.lines },
                  { key: "share", label: "Share", align: "right", render: (r) => `${(r.share*100).toFixed(1)}%` },
                  { key: "importance", label: "Importance", align: "right", render: (r) => (r.importance ?? "—") },
                ]}
                rows={charLines}
                empty="Nenhum personagem com falas encontradas nas cenas parseadas."
              />
            </div>

            <div>
              <SectionTitle
                title="Time of day (cenas)"
                subtitle="Distribuição de cenas por período. Ajuda a ler a atmosfera geral."
              />
              <Plot
                data={[{
                  type: "pie",
                  values: timeOfDayDist.map(x => x.value),
                  labels: timeOfDayDist.map(x => x.label),
                  textinfo: "label+percent",
                  hovertemplate: "%{label}: %{value} cenas (%{percent})<extra></extra>"
                }]}
                layout={{ margin: { t: 30, r: 20, b: 20, l: 20 }, height: 380, title: "" }}
                useResizeHandler
                style={{ width: "100%", height: "100%" }}
              />
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
                Exemplos: muitos “NIGHT” pode indicar tom mais sombrio; muito “DAY” tende a ser mais claro/aberto.
              </div>
            </div>
          </div>

          {/* Locations table */}
          <div>
            <SectionTitle
              title="Locations (cenas por local)"
              subtitle="Hubs de cenário: onde a narrativa mais acontece."
            />
            <SimpleTable
              columns={[
                { key: "rank", label: "#", align: "right", render: (r, i) => i + 1 },
                { key: "name", label: "Location" },
                { key: "scenes", label: "#Scenes", align: "right" },
              ]}
              rows={scenesPerLoc}
              empty="Sem cenas/locais mapeados."
            />
          </div>

          {/* Graph */}
          <div style={{ border: "1px solid #eee", borderRadius: 12, padding: 10, background: "#fafafa" }}>
            <SectionTitle
              title="Character Network (Top falantes)"
              subtitle="Nós coloridos e dimensionados por #Lines; arestas = co-ocorrência na mesma cena. Foco nos 12 que mais falam para legibilidade."
            />

            <div
              style={{
                width: "100%",
                height: 600,
                background: "#f3f4f6",
                borderRadius: 10,
                overflow: "hidden",
                marginBottom: 6
              }}
            >
              <CytoscapeComponent
                elements={cyElements}
                layout={cyLayout}
                stylesheet={cyStylesheet}
                style={{ width: "100%", height: "100%" }}
                cy={(cy) => {
                  cyRef.current = cy;
                  // fit inicial
                  setTimeout(() => { try { cy.fit(undefined, 60); } catch(e) {} }, 300);
                  // click -> Toast (mantém sua UX)
                  cy.on("tap", "node", (evt) => {
                    const d = evt.target.data();
                    onNodeClick?.({ name: d.label, lines: d.lines });
                  });
                }}
              />
            </div>

            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Dica: cores distintas por personagem ajudam a identificar clusters; distâncias maiores indicam pouca co-ocorrência.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MetadataTab;
